import { useState, useEffect } from "react";
import { X, Check, Target } from "lucide-react";
import { CONTEXT_TAGS } from "@/data/deeds";

// Übersetzungen für das Modal
const modalTranslations = {
  de: {
    title: "Deine Situation",
    subtitle: "Wähle Situationen für individuelle Taten",
    hint: "Mehrfachauswahl möglich",
    apply: "Anwenden",
    reset: "Zurücksetzen",
    activeFilters: "Aktive Filter",
    pendingNote: "Ab morgen aktiv"
  },
  en: {
    title: "Your Situation",
    subtitle: "Choose situations for individual deeds",
    hint: "Multiple selection possible",
    apply: "Apply",
    reset: "Reset",
    activeFilters: "Active Filters",
    pendingNote: "Active from tomorrow"
  },
  bs: {
    title: "Tvoja situacija",
    subtitle: "Odaberi situacije za individualna djela",
    hint: "Moguć višestruki odabir",
    apply: "Primijeni",
    reset: "Resetiraj",
    activeFilters: "Aktivni filteri",
    pendingNote: "Aktivno od sutra"
  },
  ar: {
    title: "موقفك",
    subtitle: "اختر المواقف للأعمال الفردية",
    hint: "الاختيار المتعدد ممكن",
    apply: "تطبيق",
    reset: "إعادة تعيين",
    activeFilters: "الفلاتر النشطة",
    pendingNote: "فعال من الغد"
  },
  id: {
    title: "Situasimu",
    subtitle: "Pilih situasi untuk amal individual",
    hint: "Pemilihan ganda dimungkinkan",
    apply: "Terapkan",
    reset: "Reset",
    activeFilters: "Filter Aktif",
    pendingNote: "Aktif mulai besok"
  },
  tr: {
    title: "Senin Durumun",
    subtitle: "Bireysel ameller için durumları seç",
    hint: "Çoklu seçim mümkün",
    apply: "Uygula",
    reset: "Sıfırla",
    activeFilters: "Aktif Filtreler",
    pendingNote: "Yarından itibaren aktif"
  }
};

export const ContextFilterModal = ({ 
  isOpen, 
  onClose, 
  language = 'de',
  selectedContexts = [],
  activeContexts = [],
  todayCompleted = false,
  onApply 
}) => {
  const [localSelection, setLocalSelection] = useState(selectedContexts);
  const t = modalTranslations[language] || modalTranslations.de;

  // Sync with prop changes
  useEffect(() => {
    setLocalSelection(selectedContexts);
  }, [selectedContexts, isOpen]);

  const toggleContext = (contextKey) => {
    setLocalSelection(prev => {
      if (prev.includes(contextKey)) {
        return prev.filter(c => c !== contextKey);
      }
      return [...prev, contextKey];
    });
  };

  const handleApply = () => {
    onApply(localSelection);
    onClose();
  };

  const handleReset = () => {
    setLocalSelection([]);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay context-filter-overlay" onClick={onClose} data-testid="context-filter-modal">
      <div 
        className="modal-content context-filter-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-group">
            <Target size={20} className="modal-icon" />
            <h2 className="modal-title">{t.title}</h2>
          </div>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            data-testid="context-filter-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Subtitle */}
        <p className="modal-subtitle">{t.subtitle}</p>
        
        {/* Pending indicator when today is completed AND filters are selected */}
        {todayCompleted && localSelection.length > 0 && (
          <p className="modal-pending-note" data-testid="pending-note">
            ⏰ {t.pendingNote}
          </p>
        )}

        {/* Context Tags Grid */}
        <div className="context-tags-grid" data-testid="context-tags-grid">
          {Object.entries(CONTEXT_TAGS).map(([key, config]) => {
            const isSelected = localSelection.includes(key);
            const label = config.label[language] || config.label.de;
            
            return (
              <button
                key={key}
                className={`context-tag-btn ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleContext(key)}
                data-testid={`context-tag-${key}`}
              >
                <span className="context-emoji">{config.emoji}</span>
                <span className="context-label">{label}</span>
                {isSelected && (
                  <span className="context-check">
                    <Check size={14} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Filters Display */}
        {localSelection.length > 0 && (
          <div className="active-filters-display">
            <span className="active-filters-label">{t.activeFilters}:</span>
            <div className="active-filters-tags">
              {localSelection.map(key => {
                const config = CONTEXT_TAGS[key];
                const label = config?.label[language] || config?.label.de || key;
                return (
                  <span key={key} className="active-filter-tag">
                    {config?.emoji} {label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button 
            className="modal-btn primary" 
            onClick={handleApply}
            data-testid="context-filter-apply"
          >
            {t.apply}
          </button>
          {localSelection.length > 0 && (
            <button 
              className="modal-btn secondary" 
              onClick={handleReset}
              data-testid="context-filter-reset"
            >
              {t.reset}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Compact Context Filter Button (for header)
export const ContextFilterButton = ({ 
  selectedContexts = [], 
  onClick,
  language = 'de'
}) => {
  const hasActiveFilters = selectedContexts.length > 0;
  
  return (
    <button 
      className={`context-filter-btn ${hasActiveFilters ? 'active' : ''}`}
      onClick={onClick}
      data-testid="context-filter-toggle"
      title={hasActiveFilters ? `${selectedContexts.length} Situationen aktiv` : 'Deine Situation'}
    >
      <Target size={16} />
      {hasActiveFilters && (
        <span className="filter-badge">{selectedContexts.length}</span>
      )}
    </button>
  );
};

export default ContextFilterModal;
